import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Shield, 
  Zap, 
  Users, 
  BarChart3,
  Server,
  Cloud,
  HeadphonesIcon,
  CheckCircle2,
  Sparkles,
  Rocket
} from 'lucide-react';
import { useState, useEffect } from 'react';
import ParticlesBackground from '../components/ParticlesBackground';

const services = [
  {
    icon: Server,
    title: 'Managed IT Services',
    description: 'Comprehensive technology support through proactive monitoring, maintenance, and management of your IT infrastructure.',
    color: 'cyan',
    gradient: 'from-cyan-500 to-blue-500'
  },
  {
    icon: BarChart3,
    title: 'IT Consulting',
    description: 'Expert analysis of your unique needs with strategic solutions to drive growth, efficiency, and competitive advantage.',
    color: 'magenta',
    gradient: 'from-fuchsia-500 to-purple-500'
  },
  {
    icon: Zap,
    title: 'Project Management',
    description: 'Efficiently plan, execute, and monitor IT projects, ensuring timely delivery and optimal resource utilization.',
    color: 'orange',
    gradient: 'from-orange-500 to-amber-500'
  }
];

const features = [
  { icon: Shield, title: 'Enterprise Security', description: 'Advanced protection for your business data' },
  { icon: Cloud, title: 'Cloud Solutions', description: 'Scalable infrastructure for modern needs' },
  { icon: HeadphonesIcon, title: '24/7 Support', description: 'Always available when you need us' },
  { icon: Users, title: 'Client-First Approach', description: 'Your success is our priority' }
];

const stats = [
  { value: '99.9%', label: 'Uptime Guarantee' },
  { value: '24/7', label: 'Support Available' },
  { value: '5+', label: 'Years Experience' },
  { value: '100%', label: 'Client Satisfaction' }
];

// Typing animation words
const typingWords = ['IT Solutions', 'Cloud Services', 'Cybersecurity', 'Digital Growth'];

// Counter animation component
const AnimatedCounter = ({ value, label }) => {
  const [count, setCount] = useState(0);
  const numericValue = parseFloat(value);
  const suffix = value.replace(/[0-9.]/g, '');
  
  useEffect(() => {
    let startTime;
    const duration = 2000;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * numericValue));
      if (progress < 1) requestAnimationFrame(animate);
    };
    
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        requestAnimationFrame(animate);
        observer.disconnect();
      }
    });
    
    const element = document.getElementById(`counter-${label}`);
    if (element) observer.observe(element);
    
    return () => observer.disconnect();
  }, [numericValue, label]);
  
  return (
    <p id={`counter-${label}`} className="text-4xl md:text-5xl font-bold gradient-text mb-2">
      {count}{suffix}
    </p>
  );
};

export const Home = () => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWordIndex((prev) => (prev + 1) % typingWords.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section min-h-[90vh] relative bg-gradient-to-b from-slate-50 to-white" data-testid="hero-section">
        <ParticlesBackground />
        
        {/* Decorative blobs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl animate-blob" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-fuchsia-400/20 rounded-full blur-3xl animate-blob delay-200" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-orange-400/10 rounded-full blur-3xl animate-blob delay-400" />

        <div className="hero-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.span 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-block px-4 py-2 bg-cyan-100 text-cyan-700 rounded-full text-sm font-semibold mb-6"
              >
                Your Trusted IT Partner
              </motion.span>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                Empowering Your Business with{' '}
                <span className="relative">
                  <motion.span
                    key={currentWordIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="gradient-text inline-block"
                  >
                    {typingWords[currentWordIndex]}
                  </motion.span>
                  <Sparkles className="absolute -top-2 -right-6 text-yellow-400 animate-pulse" size={20} />
                </span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-xl">
                We deliver efficient, reliable, and scalable IT solutions that enable businesses 
                to focus on core operations while we manage their technology needs.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="group btn-shine inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-full hover:shadow-xl hover:shadow-cyan-500/30 transition-all duration-300"
                  data-testid="hero-cta"
                >
                  Get Started
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-slate-300 text-slate-700 font-semibold rounded-full hover:border-cyan-500 hover:text-cyan-600 transition-all duration-300"
                  data-testid="hero-services-link"
                >
                  Explore Services
                </Link>
              </div>

              {/* Trust indicators */}
              <div className="mt-12 flex items-center gap-8">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map((i) => (
                    <div key={i} className={`w-10 h-10 rounded-full border-2 border-white bg-gradient-to-br ${
                      i === 1 ? 'from-cyan-400 to-blue-500' :
                      i === 2 ? 'from-fuchsia-400 to-purple-500' :
                      i === 3 ? 'from-orange-400 to-amber-500' :
                      'from-green-400 to-emerald-500'
                    }`} />
                  ))}
                </div>
                <p className="text-sm text-slate-600">
                  <span className="font-bold text-slate-900">100+ SMEs</span> trust us with their IT
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-fuchsia-500 rounded-3xl blur-2xl opacity-20 animate-pulse-glow" />
                <img
                  src="https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxzZXJ2ZXIlMjByb29tJTIwYmx1ZSUyMGxpZ2h0c3xlbnwwfHx8fDE3NzE0OTkyNzd8MA&ixlib=rb-4.1.0&q=85"
                  alt="Modern Server Room"
                  className="relative rounded-3xl shadow-2xl"
                />
                
                {/* Floating cards */}
                <motion.div 
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -left-8 top-1/4 glass p-4 rounded-xl shadow-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle2 className="text-green-600" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">99.9% Uptime</p>
                      <p className="text-xs text-slate-500">Guaranteed</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  className="absolute -right-8 bottom-1/4 glass p-4 rounded-xl shadow-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-cyan-100 rounded-lg">
                      <HeadphonesIcon className="text-cyan-600" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">24/7 Support</p>
                      <p className="text-xs text-slate-500">Always Here</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <AnimatedCounter value={stat.value} label={stat.label} />
                <p className="text-slate-400">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack Marquee */}
      <section className="py-8 bg-slate-50 overflow-hidden border-y border-slate-200">
        <div className="relative">
          <motion.div
            animate={{ x: ['0%', '-50%'] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            className="flex gap-12 items-center whitespace-nowrap"
          >
            {['Microsoft Azure', 'AWS', 'Google Cloud', 'Cisco', 'VMware', 'Dell', 'HP Enterprise', 'Fortinet', 'Microsoft Azure', 'AWS', 'Google Cloud', 'Cisco', 'VMware', 'Dell', 'HP Enterprise', 'Fortinet'].map((tech, i) => (
              <span key={i} className="text-2xl font-bold text-slate-300 hover:text-cyan-500 transition-colors cursor-default">
                {tech}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-fuchsia-100 text-fuchsia-700 rounded-full text-sm font-semibold mb-4">
              Our Services
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              What We <span className="gradient-text-alt">Offer</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive IT solutions tailored to drive your business forward
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                whileHover={{ y: -8 }}
                className="group relative bg-white rounded-2xl p-8 border-2 border-slate-100 hover:border-transparent transition-all duration-300 shadow-sm hover:shadow-xl"
                data-testid={`service-card-${index}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300`} />
                
                <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${service.gradient} mb-6`}>
                  <service.icon className="text-white" size={28} />
                </div>
                
                <h3 className="text-xl font-bold mb-3 text-slate-900">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed mb-6">{service.description}</p>
                
                <Link 
                  to="/services" 
                  className={`inline-flex items-center gap-2 font-semibold text-transparent bg-clip-text bg-gradient-to-r ${service.gradient} group-hover:gap-3 transition-all`}
                >
                  Learn More <ArrowRight size={16} className={`text-${service.color}-500`} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4">
                Why Choose Us
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Your Success is Our <span className="gradient-text">Priority</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                We combine technical expertise with a client-first approach to deliver 
                IT solutions that truly make a difference for your business.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-6">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="flex-shrink-0 p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl text-white">
                      <feature.icon size={22} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">{feature.title}</h4>
                      <p className="text-sm text-slate-600">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-500 rounded-3xl blur-2xl opacity-20" />
              <img
                src="https://images.pexels.com/photos/6170758/pexels-photo-6170758.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="IT Consulting Team"
                className="relative rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 cta-gradient text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Ready to <span className="gradient-text">Elevate</span> Your IT Infrastructure?
            </h2>
            <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
              Let's discuss how ZenithForge Technology can help transform your business 
              with intelligent, cost-effective IT solutions.
            </p>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 px-10 py-5 bg-white text-slate-900 font-bold rounded-full hover:shadow-2xl hover:shadow-white/20 transition-all duration-300"
              data-testid="cta-contact"
            >
              Contact Us Today
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
